import { useState } from 'react'
import { AlertTriangle, Clock, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { RateLimitError } from '@/lib/api'

/**
 * Honest failure state for a view whose data query errored — never let a
 * failed fetch masquerade as an empty result. `retry/2` is the
 * default TanStack retry, so by the time this shows the fetch really failed.
 * A rate-limit error gets its own message telling the user when to retry.
 */
export function ViewError({
  what = 'data',
  onRetry,
  error,
}: {
  what?: string
  // May return the refetch promise — we await it so the button can show a
  // pending state (a rate-limited retry re-errors instantly otherwise, making
  // a working button look dead).
  onRetry: () => unknown
  error?: unknown
}) {
  const [retrying, setRetrying] = useState(false)
  const retry = async () => {
    setRetrying(true)
    try {
      await onRetry()
    } finally {
      setRetrying(false)
    }
  }

  if (error instanceof RateLimitError) {
    // Absolute time, not "in N min": `Date.now()` is impure in render, and the
    // clock time stays correct however long the error sits on screen.
    const at = error.resetAt
      ? new Date(error.resetAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : null
    return (
      <div className="text-muted-foreground m-auto flex max-w-sm flex-col items-center gap-3 p-10 text-center text-sm">
        <Clock className="text-muted-foreground size-6" />
        <div className="space-y-1">
          <p>GitHub's hourly usage limit has been reached.</p>
          <p className="text-faint-foreground text-xs">
            {at
              ? `It resets around ${at} — it's the shared GitHub limit for everyone using Work, not yours alone.`
              : 'It resets automatically — please try again shortly.'}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={retry} disabled={retrying}>
          {retrying && <Loader2 className="size-3.5 animate-spin" />}
          {retrying ? 'Retrying…' : 'Try again'}
        </Button>
      </div>
    )
  }
  return (
    <div className="text-muted-foreground m-auto flex flex-col items-center gap-3 p-10 text-center text-sm">
      <AlertTriangle className="text-destructive size-6" />
      <p>Couldn't load {what}.</p>
      <Button size="sm" variant="outline" onClick={retry} disabled={retrying}>
        {retrying && <Loader2 className="size-3.5 animate-spin" />}
        {retrying ? 'Retrying…' : 'Retry'}
      </Button>
    </div>
  )
}
